import { BusinessEntity } from "@/hooks/useBusinesses";

interface Props {
  current: Record<string, unknown>;
  proposed: Record<string, unknown>;
}

const FIELD_LABELS: Record<string, string> = {
  businessName: "Business name",
  nameEn: "Display name (English)",
  nameMl: "Display name (Malayalam)",
  descriptionEn: "Description (English)",
  descriptionMl: "Description (Malayalam)",
  phone: "Phone",
  email: "Email",
  whatsapp: "WhatsApp",
  address: "Address",
  city: "City",
  district: "District",
  state: "State",
  pincode: "Pincode",
  lat: "Latitude",
  lng: "Longitude",
  careTypes: "Care types",
  amenityIds: "Amenities",
  totalBeds: "Total beds",
  bhkBreakdown: "BHK breakdown",
  operatingHours: "Operating hours",
  staffLanguages: "Staff languages",
  bankAccount: "Bank account",
  upiId: "UPI ID",
  gstNumber: "GST number",
  panNumber: "PAN number",
  tradeLicenseNumber: "Trade license",
  fssaiNumber: "FSSAI number",
  shopEstablishmentNumber: "Shop establishment",
  isMedicalProfessional: "Medical professional",
  medicalRegistrationNo: "Medical registration",
  medicalCouncil: "Medical council",
  medicalQualifications: "Medical qualifications",
};

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v, null, 2);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

const equal = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null && b == null) return true;
  if (typeof a === "object" || typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }
  return String(a ?? "") === String(b ?? "");
};

export const EditRequestDiff = ({ current, proposed }: Props) => {
  const keys = Object.keys(proposed);
  if (keys.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No changes in this request.
      </p>
    );
  }

  return (
    <div className="divide-y border border-border rounded-card overflow-hidden">
      <div className="grid grid-cols-12 gap-2 bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
        <div className="col-span-3">Field</div>
        <div className="col-span-4">Current</div>
        <div className="col-span-5">Proposed</div>
      </div>
      {keys.map((key) => {
        const curr = (current as Record<string, unknown>)[key];
        const prop = proposed[key];
        const unchanged = equal(curr, prop);
        return (
          <div
            key={key}
            className={`grid grid-cols-12 gap-2 px-3 py-2 text-sm ${
              unchanged ? "opacity-60" : ""
            }`}
          >
            <div className="col-span-3 font-medium">
              {FIELD_LABELS[key] ?? key}
            </div>
            <div className="col-span-4 whitespace-pre-wrap break-words text-muted-foreground">
              {formatValue(curr)}
            </div>
            <div
              className={`col-span-5 whitespace-pre-wrap break-words ${
                unchanged ? "" : "text-foreground font-medium"
              }`}
            >
              {formatValue(prop)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export type { BusinessEntity };
